import WixBaseEvent from 'ui/events/WixBaseEvent';

/**
 * @callback $w.EventHandler
 * @description Handles events.
 * @param {$w.Event} event The event that occurred.
 * @param {$w.$w} $w Deprecated: A selector function. The $w function was used to
 *  enable event handlers to work with elements in [repeaters]($w.Repeater.html). Now, to get
 *  a scoped selector for working with repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`.
 */

/**
 * @class Event
 * @summary Events are fired when certain actions occur to elements.
 * @description
 *  Events can be caused by user interactions, such as clicking the mouse or
 *  pressing a key, or generated programmatically, such as hiding or showing an
 *  element using the [hide()]($w.HiddenMixin.html#hide) and
 *  [show()]($w.HiddenMixin.html#hide) functions.
 * @memberof $w
 */
class WixEvent extends WixBaseEvent{
    constructor(target, event) {
        super(event);
        this.target = target;
        this.syntheticEvent = event;
    }

    /**
     * @member {$w.Element} target
     * @label property
     * @syntax
     * get target(): string
     * @summary Gets the element that the event was fired on.
     * @snippet [Event-target.es6=Get the ID of the target element]
     * @memberof $w.Event
     * @readonly
     */

    /**
     * @member {external:String} type
     * @label property
     * @syntax
     * get type(): string
     * @summary Gets the type of event that was fired.
     * @snippet [Event-type.es6=Get the type of the event]
     * @memberof $w.Event
     * @readonly
     */

    /**
     * @typedef {external:Object} $w.Event~EventContext
     * @summary An object that contains information about the context in which an event was fired.
     * @property {external:String} type `"GLOBAL_SCOPE"` for events fired outside
     *  of repeaters, or `"COMPONENT_SCOPE"` for events fired from repeaters.
     * @property {external:String} itemId ID of the repeater item where the
     *  event was fired from.
     * @snippet [Event-context.es6=Get the context of the event]
     * @see [context](#context)
     * @memberof $w.Event
     */

    /**
     * @member {$w.Event~EventContext} context
     * @member {external:Object} context
     * @label property
     * @syntax
     * get context(): Object
     * @summary Gets the context in which an event was fired.
     * @summary Gets the context of the event on a repeated element.
     * @description
     *  An event's `context` contains information about the circumstances
     *  surrounding the firing of the event.
     *  The context property only appears in events on repeated elements.
     *
     *  You can use the evnet context with [$w.at()]($w.html#at) to get a
     *  selector function which selects items from a specific repeater item.
     *  It contains an object with one key:value pair. The key is "itemId" and
     *  the value is the ID of the repeated item on which the event occurred.
     * @snippet [Event-context.es6=Get the context of the event]
     * @memberof $w.Event
     * @readonly
     */

}

export default WixEvent;



// WEBPACK FOOTER //
// ./js/modules/ui/events/WixEvent.es6